import { useEffect, useState } from 'react';

import { Badge } from '@repo/ui/components/ui/badge';
import { Button } from '@repo/ui/components/ui/button';
import { ConfirmationDialog } from '@repo/ui/components/ui/confirmation-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/ui/dialog';
import { Input } from '@repo/ui/components/ui/input';
import { Label } from '@repo/ui/components/ui/label';
import { Separator } from '@repo/ui/components/ui/separator';
import { Textarea } from '@repo/ui/components/ui/textarea';
import type {
  ModerationAction,
  Photo,
  PhotoPatch,
  PhotoType,
} from '@/services/photovogue';
import { PHOTO_CATEGORIES } from '@/services/photovogue';
import {
  formatDate,
  MODERATION_ACTIONS,
} from '@/components/photovogue/moderation-actions';
import { PhotoStateBadge } from '@/components/photovogue/state-badge/component';
import { PhotoLightbox } from '@/components/photovogue/photo-lightbox/component';
import { getModerationFullUrl } from '@/services/photovogue/moderation-image';

export interface PhotoDetailProps {
  photo: Photo | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onModerate: (id: string, action: ModerationAction) => void;
  onUpdate: (id: string, patch: PhotoPatch) => Promise<Photo | null>;
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between gap-4">
    <dt className="text-cn-muted-foreground">{label}</dt>
    <dd className="text-right font-medium">{value}</dd>
  </div>
);

export const PhotoDetail = ({
  photo,
  open,
  onOpenChange,
  onModerate,
  onUpdate,
}: PhotoDetailProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [tagsText, setTagsText] = useState('');

  useEffect(() => {
    if (photo) {
      setTitle(photo.title);
      setDescription(photo.description);
      setCategory(photo.category);
      setTagsText(photo.tags.join(', '));
    }
    setEditing(false);
  }, [photo?.id]);

  const isBest = !!photo && photo.types.includes('best');

  const toggleBest = async () => {
    if (!photo) return;
    const types = isBest
      ? photo.types.filter((type) => type !== 'best')
      : ([...photo.types, 'best'] as PhotoType[]);
    // Marking Best also approves the photo (legacy behaviour).
    await onUpdate(photo.id, isBest ? { types } : { types, state: 'approved' });
  };

  const save = async () => {
    if (!photo) return;
    setSaving(true);
    const tags = tagsText
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
    await onUpdate(photo.id, { title, description, category, tags });
    setSaving(false);
    setEditing(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          {photo && (
            <div className="grid gap-6 md:grid-cols-[1.4fr_1fr]">
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setLightboxOpen(true)}
                  className="block w-full overflow-hidden rounded-lg border bg-zinc-100"
                >
                  <img
                    src={getModerationFullUrl(photo)}
                    alt={photo.title}
                    className="mx-auto max-h-[60vh] max-w-full object-contain"
                  />
                </button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLightboxOpen(true)}
                >
                  View full size
                </Button>
              </div>

              <div className="space-y-4">
                <DialogHeader className="space-y-1 text-left">
                  <DialogTitle>{photo.title}</DialogTitle>
                  <DialogDescription>
                    by {photo.photographer.name}
                  </DialogDescription>
                </DialogHeader>

                <div className="flex flex-wrap items-center gap-2">
                  <PhotoStateBadge state={photo.state} />
                  {isBest && (
                    <Badge
                      variant="outline"
                      className="border-yellow-300 bg-yellow-50 font-medium text-yellow-700"
                    >
                      Best
                    </Badge>
                  )}
                  <span className="text-xs text-cn-muted-foreground">
                    Updated {formatDate(photo.stateUpdatedAt)}
                  </span>
                </div>

                {!editing && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(true)}
                    >
                      Edit details
                    </Button>
                    <Button
                      variant={isBest ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={toggleBest}
                    >
                      {isBest ? 'Remove Best' : 'Mark Best'}
                    </Button>
                  </div>
                )}

                <Separator />

                {editing ? (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="pv-title">Title</Label>
                      <Input
                        id="pv-title"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="pv-description">Description</Label>
                      <Textarea
                        id="pv-description"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="pv-category">Category</Label>
                      <select
                        id="pv-category"
                        value={category}
                        onChange={(event) => setCategory(event.target.value)}
                        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                      >
                        {PHOTO_CATEGORIES.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="pv-tags">Tags (comma separated)</Label>
                      <Input
                        id="pv-tags"
                        value={tagsText}
                        onChange={(event) => setTagsText(event.target.value)}
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing(false)}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <dl className="space-y-2 text-sm">
                      <Row label="Category" value={photo.category} />
                      <Row
                        label="Themes"
                        value={photo.themes.join(', ') || '—'}
                      />
                      <Row label="Tags" value={photo.tags.join(', ') || '—'} />
                      <Row
                        label="Editorial"
                        value={
                          photo.types.length ? photo.types.join(', ') : '—'
                        }
                      />
                      <Row
                        label="Submitted"
                        value={formatDate(photo.createdAt)}
                      />
                    </dl>

                    <Separator />

                    <div>
                      <p className="mb-2 text-xs font-medium uppercase text-cn-muted-foreground">
                        Moderation
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {MODERATION_ACTIONS.map((item) =>
                          item.destructive ? (
                            <ConfirmationDialog
                              key={item.action}
                              title="Ban photo?"
                              description="This sets the photo state to banned."
                              actionName="Ban"
                              permission=""
                              action={() => onModerate(photo.id, item.action)}
                            >
                              <Button variant="destructive" size="sm">
                                {item.label}
                              </Button>
                            </ConfirmationDialog>
                          ) : (
                            <Button
                              key={item.action}
                              variant={item.variant}
                              size="sm"
                              onClick={() => onModerate(photo.id, item.action)}
                            >
                              {item.label}
                            </Button>
                          ),
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PhotoLightbox
        photo={photo}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    </>
  );
};

export default PhotoDetail;
